import { useCallback, useEffect, useState } from "react";
import type { StaffCandidate, StaffMember, StaffRole } from "../api/types";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Did } from "../components/Did";
import { ErrorNotice, Loading } from "../components/Notice";
import { PageHead } from "../components/PageHead";
import { RoleMenu } from "../components/RoleMenu";
import { RoleChip } from "../components/StatusChip";
import { formatDate, orDash } from "../format";
import { useAsync, useCompact, useDebounced, useDismiss } from "../hooks";
import { SECTIONS } from "../sections";
import { useSession } from "../session/SessionContext";

const DID_PATTERN = /^did:privy:[A-Za-z0-9-]{1,128}$/;
// Deliberately loose: the API validates properly; this only gates the button.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** The lookup's own floor. Asking below it trades a popover for a 400. */
const MIN_LOOKUP_CHARS = 2;

export function Staff() {
  const { api, staff: me } = useSession();
  const compact = useCompact();
  const [members, setMembers] = useState<StaffMember[] | null>(null);
  const [listError, setListError] = useState<unknown>(null);

  const reload = useCallback(async () => {
    setListError(null);
    try {
      const result = await api.staff();
      setMembers(result.staff);
    } catch (err) {
      setListError(err);
    }
  }, [api]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="page">
      <PageHead title={SECTIONS.staff.label} icon={<SECTIONS.staff.Icon />}>
        <p className="subtle">
          Grant access by email. Granting a role also marks the account as
          invited, so staff never sit in the waitlist count.
        </p>
      </PageHead>

      <GrantForm onGranted={() => void reload()} />

      <ErrorNotice error={listError} />

      <div className="panel">
        <div className="panel-head">
          <h2>Current staff</h2>
          {members ? <span className="subtle">{members.length} accounts</span> : null}
        </div>
        {members === null && !listError ? (
          <Loading what="staff" />
        ) : compact ? (
          <ul className="list">
            {(members ?? []).map((member) => (
              <StaffRow
                key={member.privyDid}
                member={member}
                isSelf={member.privyDid === me?.privyDid}
                onRevoked={() => void reload()}
                compact
              />
            ))}
          </ul>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th className="num">Since</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(members ?? []).map((member) => (
                  <StaffRow
                    key={member.privyDid}
                    member={member}
                    isSelf={member.privyDid === me?.privyDid}
                    onRevoked={() => void reload()}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function GrantForm({ onGranted }: { onGranted: () => void }) {
  const { api } = useSession();
  const [identifier, setIdentifier] = useState("");
  const [role, setRole] = useState<StaffRole>("rm");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [granted, setGranted] = useState<StaffMember | null>(null);
  const [created, setCreated] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // One field, two shapes: an email is the everyday path (it is what the
  // refusal screen tells people to hand over); a did:privy: ID still works
  // for a hire who has never signed in, or when the API reports two accounts
  // sharing an email.
  const trimmed = identifier.trim();
  const isDid = DID_PATTERN.test(trimmed);
  const wellFormed = isDid || EMAIL_PATTERN.test(trimmed);

  // The lookup lives HERE, not in the field that displays it. Its answer
  // decides what the submit button does, and the popover closes on blur — an
  // offer that vanished as the admin moved the mouse toward it would be the
  // bug this arrangement exists to avoid.
  const debounced = useDebounced(trimmed, 250);
  const searchable = !isDid && debounced.length >= MIN_LOOKUP_CHARS;
  const lookup = useAsync(
    () => (searchable ? api.staffLookup(debounced) : Promise.resolve(null)),
    [api, debounced, searchable],
  );
  const candidates = lookup.data?.candidates ?? null;
  // The keystroke that has not become a request yet. Without counting it, the
  // answer to the previous word would be read as the answer to this one.
  const searching = lookup.loading || trimmed !== debounced;

  // Nobody, anywhere, carries this address: the API would 404 a plain grant,
  // so the form offers to create the account instead, for either role.
  const offering =
    !isDid && wellFormed && !searching && !lookup.error && candidates?.length === 0;

  async function grant(createAccount: boolean) {
    if (busy || !wellFormed) return;
    setBusy(true);
    setError(null);
    setGranted(null);
    try {
      const member = await api.grantStaff(
        isDid
          ? { privyDid: trimmed, role }
          : { email: trimmed, role, ...(createAccount ? { createAccount } : {}) },
      );
      setGranted(member);
      setCreated(member.created);
      setIdentifier("");
      setConfirming(false);
      onGranted();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  // Submitting in the offer state opens the dialog and writes nothing. Only
  // its confirm sends `createAccount`.
  function submit() {
    if (offering) {
      setError(null);
      setConfirming(true);
      return;
    }
    void grant(false);
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Grant access</h2>
        <span className="subtle">
          Found by email once they have signed in. Someone who never has can be
          created here.
        </span>
      </div>
      <div className="panel-body stack">
        {granted ? (
          <div className="notice notice-ok" role="status">
            {created ? "Account created. " : null}
            {orDash(granted.email)} is now <RoleChip role={granted.role} />.
          </div>
        ) : null}
        <ErrorNotice error={error} />
        <form
          className="form-grid form-grid-hinted"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="field">
            <span className="field-label" id="grant-identifier-label">
              Email (or account ID)
            </span>
            <AccountField
              value={identifier}
              onChange={setIdentifier}
              wellFormed={wellFormed}
              labelledBy="grant-identifier-label"
              candidates={candidates}
              searching={searching}
              failed={!!lookup.error}
              offering={!!offering}
            />
          </div>
          <div className="field">
            <span className="field-label" id="grant-role-label">
              Role
            </span>
            <RoleMenu
              value={role}
              onChange={setRole}
              disabled={busy}
              labelledBy="grant-role-label"
            />
          </div>
          <div>
            <button type="submit" className="primary form-submit" disabled={busy || !wellFormed}>
              {busy
                ? "Granting..."
                : offering
                  ? "Create account and grant role"
                  : "Grant role"}
            </button>
          </div>
        </form>
      </div>

      {confirming ? (
        <ConfirmDialog
          title={`Create an account for ${trimmed}?`}
          confirmLabel={`Create and grant ${role === "admin" ? "admin" : "RM"}`}
          busyLabel="Creating..."
          busy={busy}
          onConfirm={() => void grant(true)}
          onCancel={() => setConfirming(false)}
        >
          <p>
            An account and its wallet are created now, exactly as if they had
            signed in themselves, and granted <RoleChip role={role} />.
          </p>
          <p>
            They sign in to the console and the app with a code sent to this
            address. Nothing is emailed from here.
          </p>
          <p className="dialog-warn">
            Check the spelling. An account created against a typo cannot be
            reached or removed from the console.
          </p>
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/**
 * The grant field, with the account lookup shown against it as a typeahead.
 *
 * One field, not two: the thing an admin types IS the thing they are looking
 * up, and a separate search box asked them to type the same email twice. The
 * popover answers "who is behind this email" before the write, so the
 * shared-email 409 and the no-such-account 404 are both seen rather than
 * discovered afterwards.
 *
 * It searches on fragments, the way a search box does. Waiting for a complete,
 * syntactically valid address meant the suggestions arrived exactly when they
 * had stopped being useful, and the malformed-input hint fired on every
 * half-typed name on the way there.
 *
 * The lookup itself belongs to `GrantForm`, which needs the same answer to
 * decide what its submit button does. This renders that answer; it does not
 * ask the question.
 *
 * A did:privy: ID typed here never shows a popover: the endpoint matches on
 * email, and the ID path exists precisely for accounts an email cannot
 * resolve.
 */
function AccountField({
  value,
  onChange,
  wellFormed,
  labelledBy,
  candidates,
  searching,
  failed,
  offering,
}: {
  value: string;
  onChange: (next: string) => void;
  wellFormed: boolean;
  labelledBy: string;
  candidates: StaffCandidate[] | null;
  searching: boolean;
  failed: boolean;
  /** The form is offering to create this account, so the empty answer is an
      invitation rather than a dead end. */
  offering: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);

  const trimmed = value.trim();
  const isDid = DID_PATTERN.test(trimmed);

  // Email is not unique on users, so two rows can carry the exact address
  // about to be granted: that is the 409, said in advance. A long candidate
  // list is a different thing entirely and must not raise it.
  const sharing = (candidates ?? []).filter(
    (candidate) => candidate.email?.toLowerCase() === trimmed.toLowerCase(),
  ).length;
  const duplicated = duplicatedEmails(candidates);

  // Opens on the typed value rather than the debounced one, so the popover is
  // already there holding "Searching..." when the answer lands.
  const showing =
    open && !isDid && trimmed.length >= MIN_LOOKUP_CHARS && !failed;

  function pick(candidate: StaffCandidate) {
    onChange(candidate.email ?? "");
    setOpen(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (!showing || !candidates || candidates.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => (i + 1) % candidates.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => (i - 1 + candidates.length) % candidates.length);
    } else if (event.key === "Enter") {
      const candidate = candidates[active];
      // Only swallow the Enter when it actually picks something, so the key
      // still submits the form when no candidate is highlighted.
      if (candidate) {
        event.preventDefault();
        pick(candidate);
      }
    }
  }

  return (
    <div className="combo" ref={ref}>
      <input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => {
          setFocused(true);
          setOpen(true);
        }}
        onBlur={() => setFocused(false)}
        onKeyDown={onKeyDown}
        placeholder="Search by email"
        spellCheck={false}
        autoComplete="off"
        role="combobox"
        aria-expanded={showing}
        aria-autocomplete="list"
        aria-controls="grant-candidates"
        aria-labelledby={labelledBy}
      />

      {showing ? (
        <div className="combo-pop" id="grant-candidates" role="listbox">
          {(candidates ?? []).map((candidate, index) => (
            <button
              key={candidate.userId}
              type="button"
              role="option"
              aria-selected={index === active}
              className={index === active ? "combo-option is-active" : "combo-option"}
              onMouseEnter={() => setActive(index)}
              onClick={() => pick(candidate)}
            >
              <span className="cell-strong">{orDash(candidate.email)}</span>
              {/* Only where it is the sole thing telling two rows apart. On
                  every other row the email is the answer, and the DID would
                  be noise. */}
              {candidate.email && duplicated.has(candidate.email.toLowerCase()) ? (
                <Did value={candidate.privyDid} />
              ) : null}
              <RoleChip role={candidate.role} />
            </button>
          ))}

          {searching ? <p className="combo-note">Searching...</p> : null}

          {!searching && candidates && candidates.length === 0 ? (
            <p className="combo-note">
              {offering
                ? "No account with this email yet. You can create one now and grant the role in the same step."
                : "No account matches that. They must sign in once (console or app) before a role can be granted by email."}
            </p>
          ) : null}

          {sharing > 1 ? (
            <p className="combo-note combo-note-warn">
              {sharing} accounts share this exact email, so granting by email is
              refused. Use the account ID from the person&apos;s own access
              screen instead.
            </p>
          ) : null}
        </div>
      ) : null}

      {/* Held back until the field is left alone: mid-word, every email is
          malformed, and saying so on each keystroke is noise. */}
      {!focused && trimmed && !wellFormed ? (
        <p className="field-hint">
          That does not look like an email or an account ID.
        </p>
      ) : null}

    </div>
  );
}

/** The addresses carried by more than one candidate, lowercased. */
function duplicatedEmails(candidates: StaffCandidate[] | null): Set<string> {
  const seen = new Set<string>();
  const twice = new Set<string>();
  for (const candidate of candidates ?? []) {
    const email = candidate.email?.toLowerCase();
    if (!email) continue;
    if (seen.has(email)) twice.add(email);
    seen.add(email);
  }
  return twice;
}

function StaffRow({
  member,
  isSelf,
  onRevoked,
  compact = false,
}: {
  member: StaffMember;
  isSelf: boolean;
  onRevoked: () => void;
  /** A phone's list row rather than a table row: same state, same actions. */
  compact?: boolean;
}) {
  const { api } = useSession();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function revoke() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.revokeStaff(member.privyDid);
      setConfirming(false);
      onRevoked();
    } catch (err) {
      // A 409 here is the API refusing self-revocation. Its message is the
      // explanation, so it is shown as-is rather than reworded.
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const actions = (
    <div className="stack">
      {confirming ? (
        <div className="row">
          <span className="subtle">Revoke access?</span>
          <button
            type="button"
            className="danger small"
            disabled={busy}
            onClick={() => void revoke()}
          >
            {busy ? "Revoking..." : "Yes, revoke"}
          </button>
          <button
            type="button"
            className="small"
            disabled={busy}
            onClick={() => setConfirming(false)}
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="danger small"
          onClick={() => setConfirming(true)}
        >
          {isSelf ? "Revoke (self)" : "Revoke"}
        </button>
      )}
      <ErrorNotice error={error} />
    </div>
  );

  if (compact) {
    return (
      <li className="list-row list-row-stack">
        <span className="list-main">
          <span className="list-title">
            <span className="list-title-text">{orDash(member.email)}</span>
            <RoleChip role={member.role} />
          </span>
          <span className="list-meta">
            <span>Since {formatDate(member.createdAt)}</span>
          </span>
        </span>
        {actions}
      </li>
    );
  }

  return (
    <tr>
      <td className="cell-strong">{orDash(member.email)}</td>
      <td>
        <RoleChip role={member.role} />
      </td>
      <td className="num">{formatDate(member.createdAt)}</td>
      <td>{actions}</td>
    </tr>
  );
}
